import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Template } from '../../types';
import { templateService, renderService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

interface TemplateCardProps {
  template: Template;
  onDelete: (templateId: string) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onDelete }) => {
  const { apiKey } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm('Bu template\'i silmek istediğinizden emin misiniz?')) {
      return;
    }

    setIsDeleting(true);
    try {
      await templateService.deleteTemplate(template._id);
      onDelete(template._id);
    } catch (error) {
      alert('Template silinirken hata oluştu');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 overflow-hidden">
      {/* Template Preview Area */}
      <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
        {showPreview && template.isPublic ? (
          <img
            src={renderService.getPreviewUrl(template._id)}
            alt={`${template.name} preview`}
            className="w-full h-full object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="text-center">
            <div className="text-6xl mb-2">🎨</div>
            <p className="text-gray-500 text-sm">{template.width} × {template.height}</p>
          </div>
        )}
        
        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          {template.isPublic ? (
            <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">
              Public
            </span>
          ) : (
            <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-medium">
              Private
            </span>
          )}
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 truncate">
            {template.name}
          </h3>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium"
          >
            {showPreview ? '👁️ Hide' : '👁️ Preview'}
          </button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-blue-600">{template.elements.length}</div>
            <div className="text-xs text-blue-600 font-medium">Elements</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3 text-center">
            <div className="text-2xl font-bold text-green-600">{template.usage.renders}</div>
            <div className="text-xs text-green-600 font-medium">Renders</div>
          </div>
        </div>

        {/* Dates */}
        <div className="text-xs text-gray-500 mb-6 space-y-1">
          <p>📅 Created: {formatDate(template.createdAt)}</p>
          <p>🔄 Updated: {formatDate(template.updatedAt)}</p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-2 mb-4">
          <div className="flex space-x-2">
            <Link
              to={`/editor/${template._id}`}
              className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-3 rounded-lg text-sm font-medium text-center transition-all duration-200 transform hover:scale-105"
            >
              ✏️ Edit
            </Link>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg text-sm font-medium disabled:opacity-50 transition-colors"
            >
              {isDeleting ? '⏳' : '🗑️'}
            </button>
          </div>
          
          {/* Render History Button */}
          <Link
            to={`/template/${template._id}/renders`}
            className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-4 py-3 rounded-lg text-sm font-medium text-center transition-all duration-200 transform hover:scale-105"
          >
            📊 Render Geçmişi ({template.usage.renders})
          </Link>
        </div>

        {/* API Info */}
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-600 font-medium mb-2">🔗 API Endpoint:</p>
          <div className="bg-white rounded border p-2 text-xs font-mono text-gray-700">
            <p className="truncate">POST /api/render/template</p>
            <p className="text-gray-500 mt-1">Body: {"{"}"templateId": "{template._id}"{"}"}</p>
            <p className="text-gray-500 mt-1">x-api-key: {apiKey?.substring(0, 8)}...</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateCard;
